import { Link } from 'react-router-dom';
import { ArrowLeft, AlertTriangle } from 'lucide-react';
import SEO from '../components/SEO';

const LiabilityPage = () => {
  return (
    <div className="min-h-screen pt-24 pb-16 bg-slate-950" data-testid="liability-page">
      <SEO 
        title="Release of Liability"
        description="Important legal information regarding the use of Bit Buddies platform and purchase of software ideas."
        url="/liability"
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/" className="inline-flex items-center text-slate-400 hover:text-white mb-8 transition-colors">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Home
        </Link>

        <div className="flex items-center gap-3 mb-8">
          <AlertTriangle className="h-8 w-8 text-amber-400" />
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-white">Release of Liability</h1>
        </div>

        <div className="prose prose-invert max-w-none">
          <p className="text-slate-400 mb-6">Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>

          <div className="p-6 rounded-xl bg-amber-500/10 border border-amber-500/20 mb-8">
            <p className="text-amber-400 font-semibold">PLEASE READ THIS RELEASE OF LIABILITY CAREFULLY BEFORE USING OUR SERVICES OR PURCHASING ANY IDEAS.</p>
          </div>

          <div className="space-y-8 text-slate-300">
            <section>
              <h2 className="font-heading text-xl font-semibold text-white mb-4">1. Acknowledgment and Acceptance</h2>
              <p>By using the Bit Buddies platform, submitting inquiries, making purchases, or investing in any ideas presented on this website, you acknowledge that you have read, understood, and agree to be bound by this Release of Liability.</p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-semibold text-white mb-4">2. Nature of Ideas and Concepts</h2>
              <p>All ideas, concepts, designs, and intellectual property presented on Bit Buddies are provided for informational and commercial purposes. You acknowledge that:</p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li>Ideas are conceptual in nature and may require significant development, testing, and refinement</li>
                <li>No guarantees are made regarding the feasibility, profitability, or success of any idea</li>
                <li>Market conditions, technology, and other factors may affect the viability of any concept</li>
                <li>Past performance or projections do not guarantee future results</li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading text-xl font-semibold text-white mb-4">3. No Warranties</h2>
              <p>BIT BUDDIES EXPRESSLY DISCLAIMS ALL WARRANTIES, WHETHER EXPRESS, IMPLIED, STATUTORY, OR OTHERWISE, INCLUDING BUT NOT LIMITED TO:</p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li>Warranties of merchantability</li>
                <li>Fitness for a particular purpose</li>
                <li>Non-infringement of third-party rights</li>
                <li>Accuracy, completeness, or reliability of any information</li>
                <li>That any idea will generate revenue or profit</li>
                <li>That any idea is free from defects or errors</li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading text-xl font-semibold text-white mb-4">4. Assumption of Risk</h2>
              <p>You expressly assume all risks associated with:</p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li>Purchasing or licensing any idea from Bit Buddies</li>
                <li>Investing in the development of any concept</li>
                <li>Implementing, developing, or commercializing any idea</li>
                <li>Any business decisions made based on information from this platform</li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading text-xl font-semibold text-white mb-4">5. Release and Waiver</h2>
              <p>To the fullest extent permitted by applicable law, you hereby RELEASE, WAIVE, DISCHARGE, AND COVENANT NOT TO SUE Bit Buddies, its owners, officers, employees, agents, and affiliates from any and all liability, claims, demands, actions, or causes of action arising out of or related to:</p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li>Any loss, damage, or injury resulting from your use of our services</li>
                <li>Any financial losses from purchases or investments</li>
                <li>Failure of any idea to perform as expected</li>
                <li>Third-party claims related to ideas you have purchased or licensed</li>
                <li>Any indirect, incidental, special, consequential, or punitive damages</li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading text-xl font-semibold text-white mb-4">6. Limitation of Liability</h2>
              <p>IN NO EVENT SHALL BIT BUDDIES' TOTAL LIABILITY TO YOU FOR ALL DAMAGES, LOSSES, AND CAUSES OF ACTION EXCEED THE AMOUNT PAID BY YOU, IF ANY, FOR ACCESSING OR USING OUR SERVICES.</p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-semibold text-white mb-4">7. Indemnification</h2>
              <p>You agree to indemnify, defend, and hold harmless Bit Buddies and its affiliates from and against any claims, liabilities, damages, losses, and expenses (including reasonable attorneys' fees) arising out of or in any way connected with:</p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li>Your access to or use of our services</li>
                <li>Your violation of these terms</li>
                <li>Your violation of any third-party rights</li>
                <li>Your implementation or commercialization of any purchased ideas</li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading text-xl font-semibold text-white mb-4">8. Due Diligence</h2>
              <p>You acknowledge that you are solely responsible for conducting your own due diligence before making any purchase or investment decision. Bit Buddies recommends that you:</p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li>Consult with qualified professionals (attorneys, accountants, technical experts)</li>
                <li>Conduct independent market research</li>
                <li>Evaluate all risks before committing to any purchase or investment</li>
                <li>Not invest more than you can afford to lose</li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading text-xl font-semibold text-white mb-4">9. Intellectual Property Disclaimer</h2>
              <p>While Bit Buddies makes reasonable efforts to ensure the originality of ideas presented, we do not warrant that any idea is free from third-party intellectual property claims. Buyers and investors are responsible for conducting their own patent, trademark, and copyright searches.</p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-semibold text-white mb-4">10. Severability</h2>
              <p>If any provision of this Release of Liability is found to be unenforceable or invalid, such provision shall be limited or eliminated to the minimum extent necessary, and the remaining provisions shall remain in full force and effect.</p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-semibold text-white mb-4">11. Governing Law and Jurisdiction</h2>
              <p>This Release of Liability shall be governed by and construed in accordance with the laws of the State of Texas. Any disputes arising under this agreement shall be resolved exclusively in the courts of Dallas County, Texas.</p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-semibold text-white mb-4">12. Contact Information</h2>
              <p>For questions about this Release of Liability, please contact us at:</p>
              <p className="mt-2">
                <strong className="text-white">Email:</strong> MDH@BitBuddies.online<br />
                <strong className="text-white">Location:</strong> Addison, TX
              </p>
            </section>

            <section className="pt-4 border-t border-white/10">
              <p className="text-sm text-slate-500">BY USING BIT BUDDIES' SERVICES, YOU ACKNOWLEDGE THAT YOU HAVE READ THIS RELEASE OF LIABILITY, UNDERSTAND IT, AND AGREE TO BE BOUND BY ITS TERMS AND CONDITIONS.</p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiabilityPage;
